import styled from 'styled-components'

/**
 * Dashboard da home.
 *
 * A paleta e as medidas são as mesmas da identidade nova já aplicada no menu,
 * no cabeçalho, no centro de notificações e no cartão das listagens
 * (`.list-card` do TableComponent): título #101828, texto #344054, apoio
 * #5b6b7f, rótulo #98a2b3/#b0b7c3, azul #1b56a3/#2c5f9e, hairline #f0f2f5,
 * borda de cartão #e5e7eb.
 *
 * Tudo em px de propósito: a raiz deste projeto não é 16px (globals.ts reduz
 * html para 88% em telas até 1920px), então rem daria tamanhos diferentes de
 * monitor para monitor.
 *
 * O fundo da área de conteúdo é #fdfdfd — quase branco. Por isso o cartão se
 * separa do fundo pela BORDA, não pela sombra; a sombra entra só como um
 * reforço de 1px, igual ao `.list-card`.
 */

const isLight = (props: any) => props.theme.name === 'light'

export const Container = styled.main`
  /* ------------------------------------------------------------------ *
   * Cabeçalho da página: saudação + atalhos
   * ------------------------------------------------------------------ */
  .dash-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 14px 18px;
    padding-bottom: 16px;
    margin-bottom: 18px;
    border-bottom: 1px solid
      ${p => (isLight(p) ? '#f0f2f5' : 'rgba(255,255,255,.12)')};
  }

  .dash-head__id {
    display: flex;
    align-items: center;
    gap: 13px;
    min-width: 0;
  }

  /* A animação nasce em 90x90; o quadro recorta e centraliza para ela virar
     um ícone de marca em vez de um bloco solto ao lado do texto. */
  .dash-head__art {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: 13px;
    background: ${p => (isLight(p) ? '#eaf1fa' : 'rgba(143,184,234,.15)')};
    overflow: hidden;
    > div {
      margin: 0 !important;
      pointer-events: none;
    }
  }

  .dash-head__greeting {
    display: block;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.25;
    color: ${p => (isLight(p) ? '#101828' : '#fff')};
  }

  .dash-head__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 7px;
    margin-top: 3px;
    font-size: 12px;
    font-weight: 400;
    color: ${p => (isLight(p) ? '#98a2b3' : 'rgba(255,255,255,.55)')};
    svg {
      margin: 0 3px 0 0;
      font-size: 13px;
      vertical-align: -1px;
    }
    .dash-head__dot {
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background: currentColor;
      opacity: 0.6;
    }
  }

  .dash-head__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  /* Mesmo botão de contorno do rodapé do centro de notificações. */
  .dash-action {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 8px 14px;
    border: 1px solid #cfe0f3;
    border-radius: 9px;
    background: ${p => (isLight(p) ? '#fff' : 'rgba(255,255,255,.06)')};
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    color: #2c5f9e;
    transition: background-color 120ms, border-color 120ms;
    svg {
      margin: 0;
      font-size: 15px;
    }
    &:hover {
      background: ${p => (isLight(p) ? '#f2f7fd' : 'rgba(255,255,255,.12)')};
      border-color: #a9caec;
    }
    &:focus-visible {
      outline: 2px solid #2f5fa0;
      outline-offset: 2px;
    }
    /* O botão de configurar widgets não disputa atenção com os atalhos. */
    &.is-neutral {
      border-color: ${p => (isLight(p) ? '#e5e7eb' : 'rgba(255,255,255,.18)')};
      color: ${p => (isLight(p) ? '#5b6b7f' : 'rgba(255,255,255,.75)')};
      &:hover {
        background: ${p => (isLight(p) ? '#f7f9fc' : 'rgba(255,255,255,.12)')};
        border-color: ${p => (isLight(p) ? '#d6dbe2' : 'rgba(255,255,255,.3)')};
        color: ${p => (isLight(p) ? '#344054' : '#fff')};
      }
    }
  }

  .dash-head__sep {
    width: 1px;
    height: 24px;
    margin: 0 2px;
    background: ${p => (isLight(p) ? '#e5e7eb' : 'rgba(255,255,255,.18)')};
  }

  /* ------------------------------------------------------------------ *
   * Cartão
   * ------------------------------------------------------------------ */
  .dash-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${p => (isLight(p) ? '#fff' : 'rgba(255,255,255,.04)')};
    border: 1px solid ${p => (isLight(p) ? '#e5e7eb' : 'rgba(255,255,255,.15)')};
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
    overflow: hidden;
  }

  .dash-card__header {
    display: flex;
    align-items: center;
    gap: 11px;
    flex-shrink: 0;
    padding: 13px 16px;
    border-bottom: 1px solid
      ${p => (isLight(p) ? '#f0f2f5' : 'rgba(255,255,255,.12)')};
  }

  .dash-card__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #eaf1fa;
    color: #1b56a3;
    svg {
      margin: 0;
      font-size: 18px;
    }
    &.is-green {
      background: #e7f6ec;
      color: #0f7b3f;
    }
    &.is-amber {
      background: #fef3e2;
      color: #b54708;
    }
    &.is-cyan {
      background: #e8f4fd;
      color: #0b6fae;
    }
    &.is-slate {
      background: #f1f2f4;
      color: #5b6b7f;
    }
  }

  /* Título e apoio em uma linha cada: o cabeçalho tem a mesma altura em todos
     os cartões, então os corpos (e os gráficos) começam alinhados. */
  .dash-card__title {
    min-width: 0;
    flex: 1;
    strong,
    span {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    strong {
      font-size: 13.5px;
      font-weight: 700;
      line-height: 1.3;
      color: ${p => (isLight(p) ? '#101828' : '#fff')};
    }
    span {
      margin-top: 1px;
      font-size: 11.5px;
      font-weight: 400;
      line-height: 1.35;
      color: ${p => (isLight(p) ? '#98a2b3' : 'rgba(255,255,255,.55)')};
    }
  }

  /* Período / recorte do widget, à direita do título. */
  .dash-pill {
    flex-shrink: 0;
    padding: 3px 9px;
    border: 1px solid #cfe0f3;
    border-radius: 1rem;
    background: #f2f7fd;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    color: #2c5f9e;
  }

  .dash-card__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    /* Sem isto o conteúdo do flex nunca encolhe e o gráfico estoura o cartão
       — era o que acontecia nos widgets de NFe/CTe. */
    min-height: 0;
    padding: 14px 16px;
  }

  /* Listas encostam nas bordas: a linha inteira é o alvo do clique. */
  .dash-card__body.is-flush {
    position: relative;
    padding: 0;
    /* Véu no fim da lista. Sem ele a última linha visível fica cortada no meio
       e parece defeito, em vez de "tem mais, role". */
    &::after {
      content: '';
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 26px;
      pointer-events: none;
      background: linear-gradient(
        to bottom,
        ${p => (isLight(p) ? 'rgba(255, 255, 255, 0)' : 'rgba(26, 32, 44, 0)')},
        ${p => (isLight(p) ? '#fff' : '#1a202c')}
      );
    }
  }

  /* A barra de rolagem nativa (17px, cinza do sistema) é mais pesada que o
     cartão inteiro. Esta é a única do projeto; fica contida aqui dentro. */
  .dash-card__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: #dbe3ec transparent;
    &::-webkit-scrollbar {
      width: 10px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      border: 3px solid transparent;
      border-radius: 999px;
      background: #dbe3ec;
      background-clip: content-box;
    }
    &:hover::-webkit-scrollbar-thumb {
      background: #c3cdda;
      background-clip: content-box;
    }
  }

  /* O nivo mede o pai: ele precisa de uma caixa com altura resolvida. */
  .dash-chart {
    position: relative;
    flex: 1;
    min-height: 0;
    > .dash-empty {
      height: 100%;
    }
  }

  .dash-chart__label {
    flex-shrink: 0;
    margin-bottom: 2px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #b0b7c3;
  }

  /* Dois gráficos lado a lado dentro do mesmo cartão. */
  .dash-split {
    display: flex;
    flex: 1;
    min-height: 0;
    gap: 20px;
    > div {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      min-height: 0;
    }
  }

  @media (max-width: 991px) {
    .dash-split {
      flex-direction: column;
      gap: 14px;
    }
  }

  /* ------------------------------------------------------------------ *
   * Indicadores
   * ------------------------------------------------------------------ */
  .dash-kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 10px;
    flex-shrink: 0;
    margin-bottom: 14px;
  }

  /* No cartão de largura inteira, 1fr esticaria cada indicador por 350px e o
     número ficaria perdido na esquerda da caixa. Só a partir de 1400px: em
     telas menores o teto de 240px faria o quarto indicador cair de linha. */
  @media (min-width: 1400px) {
    .dash-kpis.is-contained {
      grid-template-columns: repeat(auto-fit, minmax(150px, 240px));
    }
  }

  .dash-kpi {
    padding: 9px 12px;
    border: 1px solid ${p => (isLight(p) ? '#f0f2f5' : 'rgba(255,255,255,.12)')};
    border-radius: 10px;
    background: ${p => (isLight(p) ? '#fbfcfd' : 'rgba(255,255,255,.04)')};
  }

  .dash-kpi__label {
    display: block;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    line-height: 1;
    text-transform: uppercase;
    color: #98a2b3;
  }

  .dash-kpi__value {
    display: block;
    margin-top: 6px;
    font-size: 19px;
    font-weight: 800;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
    color: ${p => (isLight(p) ? '#101828' : '#fff')};
    &.is-accent {
      color: #0f7b3f;
    }
  }

  /* ------------------------------------------------------------------ *
   * Barra de proporção + legenda
   * ------------------------------------------------------------------ */
  .dash-progress {
    display: flex;
    flex-shrink: 0;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    background: ${p => (isLight(p) ? '#f0f2f5' : 'rgba(255,255,255,.12)')};
    > span {
      display: block;
      height: 100%;
      transition: width 300ms;
    }
  }

  .dash-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 5px 14px;
    flex-shrink: 0;
    margin-top: 9px;
  }

  .dash-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: ${p => (isLight(p) ? '#5b6b7f' : 'rgba(255,255,255,.65)')};
    i {
      width: 8px;
      height: 8px;
      border-radius: 3px;
      flex-shrink: 0;
    }
    b {
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: ${p => (isLight(p) ? '#344054' : '#fff')};
    }
  }

  /* ------------------------------------------------------------------ *
   * Linhas de lista
   *
   * Em vez de <table>, que herdaria do globals.ts o padding !important, a
   * zebra nas ímpares e o cursor de linha clicável. Aqui a linha é um
   * <button>: um alvo de clique de verdade, com foco de teclado.
   * ------------------------------------------------------------------ */
  .dash-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 16px;
    border: none;
    background: none;
    text-align: left;
    transition: background-color 120ms;
    & + .dash-row {
      border-top: 1px solid
        ${p => (isLight(p) ? '#f4f6f8' : 'rgba(255,255,255,.08)')};
    }
    &:hover {
      background: ${p => (isLight(p) ? '#f7f9fc' : 'rgba(255,255,255,.06)')};
      .dash-row__chevron {
        color: #2c5f9e;
        transform: translateX(2px);
      }
    }
    &:focus-visible {
      outline: 2px solid #2f5fa0;
      outline-offset: -2px;
    }
  }

  .dash-row__main {
    flex: 1;
    min-width: 0;
  }

  .dash-row__title {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: ${p => (isLight(p) ? '#101828' : '#fff')};
  }

  .dash-row__sub {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    overflow: hidden;
    font-size: 11.5px;
    line-height: 1.3;
    white-space: nowrap;
    color: ${p => (isLight(p) ? '#98a2b3' : 'rgba(255,255,255,.55)')};
    > span {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .dash-row__side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
  }

  .dash-row__value {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: ${p => (isLight(p) ? '#101828' : '#fff')};
    &.is-green {
      color: #0f7b3f;
    }
  }

  .dash-row__date {
    margin-top: 2px;
    font-size: 11px;
    line-height: 1.3;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: ${p => (isLight(p) ? '#98a2b3' : 'rgba(255,255,255,.55)')};
  }

  .dash-row__chevron {
    flex-shrink: 0;
    margin: 0;
    font-size: 17px;
    color: #cfd6e0;
    transition: color 120ms, transform 120ms;
  }

  /* Estado em texto, no lugar do Badge saturado do bootstrap. */
  .dash-tag {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
    background: #f1f2f4;
    color: #4b5563;
    &.is-green {
      background: #e7f6ec;
      color: #0f7b3f;
    }
    &.is-blue {
      background: #eaf0fc;
      color: #2c4e9e;
    }
    &.is-amber {
      background: #fef3e2;
      color: #b54708;
    }
  }

  /* ------------------------------------------------------------------ *
   * Vazio e carregamento
   * ------------------------------------------------------------------ */
  .dash-empty {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 26px 20px;
    text-align: center;
    svg {
      margin: 0 0 5px;
      font-size: 28px;
      color: #cfd6e0;
    }
    strong {
      font-size: 13px;
      font-weight: 700;
      color: ${p => (isLight(p) ? '#344054' : '#fff')};
    }
    span {
      font-size: 12px;
      color: #98a2b3;
    }
  }

  .dash-skeleton {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 16px;
    & + .dash-skeleton {
      border-top: 1px solid
        ${p => (isLight(p) ? '#f4f6f8' : 'rgba(255,255,255,.08)')};
    }
    i {
      display: block;
      height: 9px;
      border-radius: 5px;
      background: ${p => (isLight(p) ? '#eef1f5' : 'rgba(255,255,255,.12)')};
      animation: dashPulse 1200ms ease-in-out infinite;
      &:first-child {
        flex: 1;
      }
      &:last-child {
        width: 58px;
      }
    }
  }

  @keyframes dashPulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }

  /* Nenhum widget escolhido. */
  .dash-blank {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 58vh;
    padding: 40px 20px;
    border: 1px dashed ${p => (isLight(p) ? '#dbe3ec' : 'rgba(255,255,255,.2)')};
    border-radius: 14px;
    background: ${p => (isLight(p) ? '#fbfcfd' : 'rgba(255,255,255,.03)')};
    text-align: center;
    > svg {
      margin: 0 0 6px;
      font-size: 38px;
      color: #cfd6e0;
    }
    h5 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: ${p => (isLight(p) ? '#344054' : '#fff')};
    }
    p {
      margin: 0 0 14px;
      font-size: 13px;
      color: #98a2b3;
    }
  }

  /* ------------------------------------------------------------------ *
   * Grade
   * ------------------------------------------------------------------ */
  .dash-grid {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -8px;
  }

  /* As colunas do bootstrap vêm com flex: 0 0 X% e max-width: X%. Soltar o
     crescimento (com flex-grow proporcional a cols, definido no elemento)
     faz a linha fechar os 12 mesmo quando o usuário desliga um widget — sem
     isso, esconder "Novos Cadastros" deixava um vão de 4 colunas na direita. */
  .dash-cell {
    display: flex;
    /* Com max-width solto, o item de flex volta a respeitar o min-content do
       conteúdo (min-width: auto) e um nome de cedente comprido empurraria o
       cartão para a linha seguinte. */
    min-width: 0;
    max-width: none;
    padding: 0 8px;
    margin-bottom: 16px;
    > * {
      width: 100%;
    }
  }

  /* O breakpoint lg do bootstrap olha a janela, mas quem manda na largura do
     cartão é a área de conteúdo — o menu come 260px dela. Em 1100px de janela,
     "Operações Pagas" (3 de 12) ficava com 200px: nome do cedente e valor não
     cabiam na mesma linha. Entre lg e 1400px os widgets estreitos passam a
     meia largura e os largos ocupam a linha toda. */
  @media (min-width: 992px) and (max-width: 1399px) {
    .dash-cell.col-lg-3,
    .dash-cell.col-lg-4,
    .dash-cell.col-lg-5 {
      flex-basis: 50%;
    }
    .dash-cell.col-lg-7,
    .dash-cell.col-lg-8,
    .dash-cell.col-lg-9,
    .dash-cell.col-lg-10,
    .dash-cell.col-lg-11 {
      flex-basis: 100%;
    }
  }
`
